import React, { useState, useEffect } from 'react';
import { AlertTriangle, AlertCircle, CheckCircle, Clock, TrendingDown, Target } from 'lucide-react';
import { Metric, MetricForecast } from '../../types';

interface MetricAlertsProps {
  metric: Metric;
  forecast: MetricForecast | null;
}

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  icon: React.ReactNode;
  timestamp: string;
}

export const MetricAlerts: React.FC<MetricAlertsProps> = ({ metric, forecast }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  useEffect(() => {
    generateAlerts();
  }, [metric, forecast]);

  const generateAlerts = () => {
    const newAlerts: Alert[] = [];
    const now = new Date().toISOString();

    // Target deadline approaching
    if (metric.targetDate) {
      const daysLeft = Math.ceil((new Date(metric.targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysLeft <= 7 && daysLeft > 0) {
        newAlerts.push({
          id: 'deadline-warning',
          type: 'warning',
          title: 'Deadline Approaching',
          message: `Target deadline is in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`,
          icon: <Clock className="w-4 h-4" />,
          timestamp: now
        });
      } else if (daysLeft <= 0) {
        newAlerts.push({
          id: 'deadline-critical',
          type: 'critical',
          title: 'Deadline Passed',
          message: `Target deadline was ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago`,
          icon: <AlertTriangle className="w-4 h-4" />,
          timestamp: now
        });
      }
    }

    // Target achievement risk
    if (forecast && metric.targetValue) {
      if (forecast.willMeetTarget === false && forecast.confidence > 0.6) {
        newAlerts.push({
          id: 'target-risk',
          type: 'critical',
          title: 'Target Achievement at Risk',
          message: `${Math.round(forecast.confidence * 100)}% confidence that target will not be met`,
          icon: <Target className="w-4 h-4" />,
          timestamp: now
        });
      } else if (forecast.willMeetTarget === false && forecast.confidence > 0.3) {
        newAlerts.push({
          id: 'target-warning',
          type: 'warning',
          title: 'Target Achievement Uncertain',
          message: `${Math.round(forecast.confidence * 100)}% confidence of missing target`,
          icon: <AlertCircle className="w-4 h-4" />,
          timestamp: now
        });
      }
    }

    // Negative trend detection
    if (forecast && forecast.trend === 'down' && forecast.dailyChangeRate && forecast.dailyChangeRate < 0) {
      const daysToZero = metric.currentValue / Math.abs(forecast.dailyChangeRate);
      
      if (daysToZero < 30) {
        newAlerts.push({
          id: 'negative-trend',
          type: 'critical',
          title: 'Negative Trend Detected',
          message: `At current rate, value will reach zero in ${Math.ceil(daysToZero)} days`,
          icon: <TrendingDown className="w-4 h-4" />,
          timestamp: now
        });
      }
    }

    // Low confidence in forecast
    if (forecast && forecast.confidence < 0.3) {
      newAlerts.push({
        id: 'low-confidence',
        type: 'warning',
        title: 'Low Forecast Confidence',
        message: `Forecast confidence is only ${Math.round(forecast.confidence * 100)}%. Consider adding more data points.`,
        icon: <AlertCircle className="w-4 h-4" />,
        timestamp: now
      });
    }

    // Progress stagnation (if no recent updates)
    if (metric.lastUpdate) {
      const daysSinceUpdate = Math.floor((new Date().getTime() - new Date(metric.lastUpdate).getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysSinceUpdate >= 7) {
        newAlerts.push({
          id: 'stagnation',
          type: 'info',
          title: 'No Recent Updates',
          message: `No new values recorded for ${daysSinceUpdate} days`,
          icon: <Clock className="w-4 h-4" />,
          timestamp: now
        });
      }
    }

    // Target achievement success
    if (metric.targetValue && metric.currentValue >= metric.targetValue) {
      newAlerts.push({
        id: 'target-achieved',
        type: 'info',
        title: 'Target Achieved',
        message: `Current value (${metric.currentValue}) has reached the target (${metric.targetValue})`,
        icon: <CheckCircle className="w-4 h-4" />,
        timestamp: now
      });
    }

    setAlerts(newAlerts);
  };

  const getAlertStyle = (type: string) => {
    switch (type) {
      case 'critical':
        return 'border-red-200 bg-red-50 text-red-800';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50 text-yellow-800';
      case 'info':
        return 'border-blue-200 bg-blue-50 text-blue-800';
      default:
        return 'border-gray-200 bg-gray-50 text-gray-800';
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'critical':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      case 'info':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  if (alerts.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          Metric Health
        </h4>
        <div className="text-center text-green-600 py-4">
          <CheckCircle className="w-8 h-8 mx-auto mb-2" />
          <p className="font-medium">All Good!</p>
          <p className="text-sm text-gray-600 mt-1">No alerts or concerns for this metric</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-orange-500" />
        Metric Alerts
        <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
          {alerts.length}
        </span>
      </h4>

      <div className="space-y-3">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`border rounded-lg p-4 ${getAlertStyle(alert.type)}`}
          >
            <div className="flex items-start gap-3">
              <div className={getIconColor(alert.type)}>
                {alert.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h5 className="font-semibold">{alert.title}</h5>
                  <span className="text-xs opacity-75">
                    {new Date(alert.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm">{alert.message}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center text-sm">
          <div>
            <div className="text-red-600 font-semibold">
              {alerts.filter(a => a.type === 'critical').length}
            </div>
            <div className="text-gray-600">Critical</div>
          </div>
          <div>
            <div className="text-yellow-600 font-semibold">
              {alerts.filter(a => a.type === 'warning').length}
            </div>
            <div className="text-gray-600">Warning</div>
          </div>
          <div>
            <div className="text-blue-600 font-semibold">
              {alerts.filter(a => a.type === 'info').length}
            </div>
            <div className="text-gray-600">Info</div>
          </div>
        </div>
      </div>
    </div>
  );
};